import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/components/ui/inputs/Checkbox';
import { Text } from '@/components/ui/text/Text';
import { IconScout } from '@/components/ui/images/IconScout';
import { LocalIcon } from '@/components/ui/images/LocalIcon';
import { TABLE_PALETTE, ROW_BORDER_STYLE } from './Table.constants';
import { resolveCellContent, getAlignmentClasses, getColumnWidth } from './Table.utils';
import { TableCell } from './_TableCell';
import { ExpandedRowDetails } from './ExpandedRowDetails';
import type { TableColumn, SortState, ExpandableConfig, BulkActionItem } from './Table.types';
import { colors } from '@/assets/styles/colors';


export interface TableContainerProps<T> {
  columns: TableColumn<T>[];
  selectable: boolean;
  allVisibleSelected: boolean;
  onToggleSelectAll: () => void;
  onSort: (col: TableColumn<T>) => void;
  sort: SortState | undefined;
  children: React.ReactNode;
  disabled?: boolean;
  minTableWidth: number;
  expandable?: ExpandableConfig<T>;
  hasSelection?: boolean;
  bulkActions?: BulkActionItem[];
  isIndeterminate?: boolean;
  selectAllDisabled?: boolean;
}

/**
 * Internal component for table container with header
 * Maintains responsivity with overflow-x-auto
 */
export function TableContainer<T extends Record<string, any>>({
  columns,
  selectable,
  allVisibleSelected,
  onToggleSelectAll,
  onSort,
  sort,
  children,
  disabled = false,
  minTableWidth,
  expandable,
  hasSelection = false,
  bulkActions = [],
  isIndeterminate = false,
  selectAllDisabled = false,
}: TableContainerProps<T>) {
  const { t: translate } = useTranslation();
  const [bulkMenuOpen, setBulkMenuOpen] = React.useState(false);
  const bulkMenuRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!hasSelection) setBulkMenuOpen(false);
  }, [hasSelection]);

  React.useEffect(() => {
    if (!bulkMenuOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (bulkMenuRef.current && !bulkMenuRef.current.contains(event.target as Node)) {
        setBulkMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [bulkMenuOpen]);

  const showBulkTrigger = hasSelection && bulkActions.length > 0;

  return (
    <div style={{ position: 'relative' }}>
      <div
        className="overflow-x-auto"
        style={{
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <table
          className="border-collapse"
          style={{
            borderSpacing: 0,
            minWidth: `${minTableWidth}px`,
            width: '100%',
            tableLayout: 'fixed',
          }}
        >
          <thead>
            <tr className="text-left">
              {expandable?.showExpandColumn !== false && expandable && (
                <th className="px-4 py-2 w-10" style={ROW_BORDER_STYLE}>
                  {/* Empty header for expand column */}
                </th>
              )}
              {selectable && (
                <th
                  className="px-4 py-2"
                  style={{ ...ROW_BORDER_STYLE, width: bulkActions.length ? '64px' : '40px' }}
                >
                  <div className="flex items-center gap-1">
                    <Checkbox
                      checked={allVisibleSelected}
                      indeterminate={isIndeterminate}
                      onChange={() => !disabled && !selectAllDisabled && onToggleSelectAll()}
                      data-testid="checkbox-all"
                      disabled={disabled || selectAllDisabled}
                    />
                    {showBulkTrigger && (
                      <button
                        type="button"
                        className="flex items-center"
                        onClick={() => setBulkMenuOpen((v) => !v)}
                        data-testid="bulk-actions-trigger"
                        aria-label={translate('common:table.bulkActionsLabel')}
                        aria-expanded={bulkMenuOpen}
                      >
                        <IconScout name="angleDown" size={14} color={colors.primary.main} />
                      </button>
                    )}
                  </div>
                </th>
              )}
              {columns.map((column) => {
                const isActive = sort?.key === String(column.key);
                const baseCellClass = 'px-4 py-2 font-medium select-none whitespace-nowrap';
                const interactiveClass = column.sortable ? 'cursor-pointer' : '';
                const align = column.align ?? 'left';
                const columnWidth = getColumnWidth(column);
                const ariaSort = column.sortable
                  ? isActive
                    ? sort.direction === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : 'none'
                  : undefined;
                const { text: alignClass, justify: justifyClass } = getAlignmentClasses(align);
                return (
                  <th
                    key={String(column.key)}
                    onClick={() => column.sortable && !disabled && onSort(column)}
                    onKeyDown={(event) => {
                      if (column.sortable && (event.key === 'Enter' || event.key === ' ')) {
                        event.preventDefault();
                        onSort(column);
                      }
                    }}
                    tabIndex={column.sortable && !disabled ? 0 : -1}
                    aria-sort={ariaSort as any}
                    scope="col"
                    className={`${baseCellClass} ${interactiveClass} ${alignClass} focus:outline-none focus:ring-2`}
                    aria-disabled={disabled || undefined}
                    style={{
                      ...ROW_BORDER_STYLE,
                      width: column.width || `${columnWidth}px`,
                      minWidth: `${columnWidth}px`,
                      maxWidth: column.width || `${columnWidth}px`,
                    }}
                  >
                    <div className={`flex gap-1 items-center ${justifyClass}`}>
                      <Text variant="subheader-medium" color={TABLE_PALETTE.headerText} className="truncate">
                        {column.header}
                      </Text>
                      {column.sortable && (
                        <IconScout
                          name={isActive ? (sort.direction === 'asc' ? 'arrowUp' : 'arrowDown') : 'arrowUp'}
                          size="lg"
                          color={TABLE_PALETTE.arrow}
                          className={`transition-transform ${isActive ? '' : 'opacity-40'}`}
                        />
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          {children}
        </table>
      </div>
      {bulkMenuOpen && showBulkTrigger && (
        <div
          ref={bulkMenuRef}
          className="absolute bg-white rounded-2xl p-1 flex flex-col"
          style={{
            top: '46px',
            left: '16px',
            boxShadow: '0px 4px 16px 0px rgba(0,0,0,0.15)',
            minWidth: 'max-content',
            zIndex: 50,
          }}
          data-testid="bulk-actions-menu"
        >
          {bulkActions.map((action) => (
            <button
              key={action.label}
              type="button"
              className="flex items-center gap-3 px-3 py-1 rounded-lg hover:bg-gray-50 text-left cursor-pointer"
              onClick={() => { action.onClick(); setBulkMenuOpen(false); }}
              data-testid={`bulk-action-${action.label}`}
            >
              <IconScout
                name={action.icon}
                size={24}
                color={action.variant === 'danger' ? colors.feedback.error300 : colors.grays.neutral33}
              />
              <Text variant="subheader-regular" color={colors.grays.neutral33}>
                {action.label}
              </Text>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export interface TableBodyProps<T> {
  data: T[];
  columns: TableColumn<T>[];
  selectable: boolean;
  selectedIds: Set<string | number>;
  toggleRow: (row: T) => void;
  getRowId: (row: T) => string | number;
  disabled?: boolean;
  expandable?: ExpandableConfig<T>;
  expandedRowKeys?: Set<string | number>;
  toggleRowExpanded?: (rowId: string | number) => void;
  emptyMessage?: string;
  /** Optional predicate to disable the checkbox for specific rows */
  isRowSelectable?: (row: T) => boolean;
}

/**
 * Internal component for table body with rows and cells
 * Uses TableCell for consistent rendering with improvements
 */
export function TableBody<T extends Record<string, any>>({
  data,
  columns,
  selectable,
  selectedIds,
  toggleRow,
  getRowId,
  disabled = false,
  expandable,
  expandedRowKeys,
  toggleRowExpanded,
  emptyMessage,
  isRowSelectable,
}: TableBodyProps<T>) {
  const { t: translate } = useTranslation();
  return (
    <tbody>
      {data.length === 0 && (
        <tr>
          <td
            colSpan={
              columns.length +
              (selectable ? 1 : 0) +
              (expandable?.showExpandColumn !== false && expandable ? 1 : 0)
            }
            className="px-4 py-8 text-center"
            style={ROW_BORDER_STYLE}
          >
            <div className="flex flex-col items-center gap-3">
              <LocalIcon name="emptyList" className="w-40 h-auto" />
              <Text variant="body-title-medium" textAlign="center">{emptyMessage ?? translate('common:table.emptyTitle')}</Text>
              {!emptyMessage && (
                <Text variant="body-regular" textAlign="center">
                  {translate('common:table.emptyDescription')}
                  <br />
                  {translate('common:table.emptySearchHint')}
                </Text>
              )}
            </div>
          </td>
        </tr>
      )}
      {data.map((row) => {
        const id = getRowId(row);
        const isExpanded = expandedRowKeys?.has(id) ?? false;
        const canExpand = expandable?.rowExpandable ? expandable.rowExpandable(row) : true;
        const rowSelectable = isRowSelectable ? isRowSelectable(row) : true;
        const showExpandable = expandable && canExpand;
        
        return (
          <React.Fragment key={String(id)}>
            <tr
              className="transition-colors hover:bg-[var(--row-hover)]"
              style={ROW_BORDER_STYLE}
            >
              {expandable?.showExpandColumn !== false && expandable && (
                <td className="px-4 py-2">
                  <button
                    onClick={() => !disabled && canExpand && toggleRowExpanded?.(id)}
                    className="flex items-center justify-center w-8 h-8"
                    disabled={disabled || !canExpand}
                    aria-label={isExpanded ? 'Colapsar fila' : 'Expandir fila'}
                    data-testid={`expand-button-${id}`}
                  >
                    <IconScout 
                      name={isExpanded ? 'angleUp' : 'angleDown'} 
                      size="xl" 
                      color={(
                        disabled || !canExpand
                      ) ? colors.grays.neutralCC : colors.important.main} 
                    />
                  </button>
                </td>
              )}
              {selectable && (
                <td className="px-4 py-2">
                  <Checkbox
                    checked={selectedIds.has(id)}
                    onChange={() => !disabled && rowSelectable && toggleRow(row)}
                    data-testid={`checkbox-${id}`}
                    disabled={disabled || !rowSelectable}
                  />
                </td>
              )}
              {columns.map((column) => {
                const align = column.align ?? 'left';
                const columnWidth = getColumnWidth(column);
                const strategy = column.overflowStrategy ?? 'truncate';
                const rawValue: any = column.render
                  ? column.render(row)
                  : (row as any)[column.key as keyof T];
                const value = resolveCellContent(rawValue);
                const { text: bodyAlignClass } = getAlignmentClasses(align);
                
                const cellContent = (
                  <TableCell strategy={strategy} width={column.width || columnWidth} align={align}>
                    {value}
                  </TableCell>
                );
                
                return (
                  <td
                    key={String(column.key)}
                    className={`px-4 py-2 ${bodyAlignClass}`}
                    style={{
                      ...ROW_BORDER_STYLE,
                      width: column.width || `${columnWidth}px`,
                      minWidth: `${columnWidth}px`,
                      maxWidth: column.width || `${columnWidth}px`,
                    }}
                  >
                    {cellContent}
                  </td>
                );
              })}
            </tr>
            {showExpandable && isExpanded && (
              <tr
                style={{
                  ...ROW_BORDER_STYLE,
                  backgroundColor: TABLE_PALETTE.expandedRowBackground,
                }}
              >
                <td
                  colSpan={
                    columns.length +
                    (selectable ? 1 : 0) +
                    (expandable?.showExpandColumn !== false ? 1 : 0)
                  }
                  className="py-0"
                  style={ROW_BORDER_STYLE}
                >
                  <div className="pl-24">
                    {expandable.renderExpanded
                      ? expandable.renderExpanded(row)
                      : expandable.getExpandedFields
                        ? <ExpandedRowDetails fields={expandable.getExpandedFields(row)} />
                        : null
                    }
                  </div>
                </td>
              </tr>
            )}
          </React.Fragment>
        );
      })}
    </tbody>
  );
}
